import { useState } from 'react';
import { Link } from 'react-router-dom';

import Modal from '../../../common/components/modal/modal';
import { Gate } from '../../../global/contexts/auth/auth';
import { useIssueOrder, useSeat } from '../contexts/coordination';
import { ORDER_VERBS } from '../queries/paths';
import { onFixtures } from '../queries/source';
import { ORDER_ACTIONS, orderIntent, orderRetryable } from '../utils/operator';
import { RawJson } from './bits';

export default function ActionModal({ action, item, onClose }) {
    if (ORDER_VERBS.includes(action.key)) {
        const metadata = ORDER_ACTIONS[action.key];
        return (
            <Gate all={[metadata.permission, ...(metadata.also ?? []), 'machines:read']}>
                <OrderModal action={{ ...action, ...metadata }} item={item} onClose={onClose} />
            </Gate>
        );
    }
    return (
        <Modal
            open
            title="Revisar el trabajo"
            onClose={onClose}
            actions={
                <button type="button" className="coord__button" onClick={onClose}>
                    Cerrar
                </button>
            }
        >
            <p>
                Abre la tarea para consultar su entrega, criterios y observaciones antes de decidir.
            </p>
            <Gate domain="tasks:read">
                <Link
                    className="coord__button coord__button--primary"
                    to={
                        item?.scope?.task_id
                            ? `/admin/tasks/${item.scope.task_id}`
                            : '/admin/development/revision'
                    }
                    onClick={onClose}
                >
                    Ver tarea y revisión
                </Link>
            </Gate>
        </Modal>
    );
}

function OrderModal({ action, item, onClose }) {
    const positionId = action.target ?? item?.scope?.position_id;
    const detail = useSeat(item?.row ? null : positionId);
    const row = item?.row ?? detail.data?.seat;
    if (row) return <OrderConfirmation action={action} row={row} item={item} onClose={onClose} />;
    return (
        <Modal
            open
            title={action.label}
            onClose={onClose}
            actions={
                <button type="button" className="coord__button" onClick={onClose}>
                    Cerrar
                </button>
            }
        >
            <p role="status">
                {detail.error
                    ? detail.error.message
                    : (detail.unavailable ?? 'Consultando el estado del puesto…')}
            </p>
        </Modal>
    );
}

function OrderConfirmation({ action, row, item, onClose }) {
    // Capture exactly the state the person reviewed. Background refreshes must
    // not change the revision of an intent already presented for confirmation.
    const [snapshot] = useState(row);
    const [reason, setReason] = useState('');
    const [submitted, setSubmitted] = useState(null);
    const [result, setResult] = useState(null);
    const issue = useIssueOrder(snapshot.position_id);
    const retryable = orderRetryable(issue.error);
    const missingRevision = snapshot.seat?.revision == null;
    const missingTask =
        ['interrumpir', 'reintentar'].includes(action.key) &&
        !(item?.scope?.task_id ?? snapshot.work?.task?.id);
    const run = async () => {
        const body = orderIntent(submitted, {
            verb: action.key,
            row: snapshot,
            taskId: item?.scope?.task_id,
            reason,
        });
        // Retrying an uncertain response reuses the exact body and expiry.
        setSubmitted(body);
        try {
            setResult(await issue.mutateAsync(body));
        } catch {
            /* ApiError is rendered below. */
        }
    };
    return (
        <Modal
            open
            title={action.label}
            busy={issue.isPending}
            onClose={onClose}
            actions={
                <>
                    <button
                        type="button"
                        className="coord__button"
                        disabled={issue.isPending}
                        onClick={onClose}
                    >
                        {result ? 'Cerrar' : 'Cancelar'}
                    </button>
                    {!result && (
                        <button
                            type="button"
                            className="coord__button coord__button--primary"
                            disabled={
                                missingRevision ||
                                missingTask ||
                                issue.isPending ||
                                (!!issue.error && !retryable)
                            }
                            onClick={run}
                        >
                            {issue.isPending
                                ? 'Enviando…'
                                : retryable
                                  ? 'Reintentar la misma solicitud'
                                  : onFixtures
                                    ? 'Simular solicitud'
                                    : 'Confirmar solicitud'}
                        </button>
                    )}
                </>
            }
        >
            <p>
                <strong>{snapshot.machine.name}</strong> ·{' '}
                {snapshot.seat.workspace_label || `puesto ${snapshot.position_id}`}
            </p>
            <p>{action.does}</p>
            {missingRevision && (
                <p role="alert" className="coord__error">
                    No se pudo leer el estado del puesto. Cierra y actualiza antes de continuar.
                </p>
            )}
            {missingTask && (
                <p role="alert" className="coord__error">
                    Este puesto no tiene una tarea identificada para esta solicitud. Abre la
                    incidencia correspondiente antes de continuar.
                </p>
            )}
            <label className="coord__field">
                <span>Motivo (opcional)</span>
                <textarea
                    className="coord__textarea"
                    rows={3}
                    maxLength={2000}
                    value={reason}
                    disabled={!!submitted}
                    onChange={e => setReason(e.target.value)}
                />
            </label>
            <p className="coord__panel-note">
                La solicitud vence en 30 minutos. El estado solo se mostrará confirmado cuando el
                equipo responda.
            </p>
            {issue.error && (
                <div role="alert" className="coord__error">
                    <p>{issue.error.message}</p>
                    <p>
                        {retryable
                            ? 'No sabemos si llegó. Puedes reintentar esta misma solicitud sin duplicarla.'
                            : 'Cierra esta ventana y actualiza el puesto antes de decidir de nuevo.'}
                    </p>
                </div>
            )}
            {result && (
                <p role="status" className="coord__success">
                    {result.sent
                        ? 'Solicitud registrada. Consulta en el puesto si ya fue recibida y confirmada por el equipo.'
                        : 'Simulación completada. No se envió ninguna orden.'}
                </p>
            )}
            {submitted && (
                <RawJson value={result ?? submitted} label="Ver detalle de la solicitud" />
            )}
        </Modal>
    );
}
